import React from "react";
import styled from "@emotion/styled";
import {useTranslation} from "react-i18next";
import {useSelector} from "react-redux";
import {AutoSizer, List, ListRowProps} from "react-virtualized";
import {getSortedPlayers} from "modules/selectors";
import {IPlayer} from "modules/types";
import {Exist} from "components";
import {PlayerPoolRow} from "./PlayerPoolRow";

const Wrapper = styled.div`
	display: flex;
	flex-direction: column;
	flex-grow: 1;
`;

const Container = styled.div`
	flex: 1;
	margin: 2px 0;

	.player-pool-list {
		overflow: auto !important; // react-joyride tutorial overwrote the value
	}
`;

const HeadRow = styled.div`
	display: flex;
	justify-content: space-between;
	background: #545456;
	color: ${({theme}) => theme.colorPalette.white};
	padding: 16px;
`;

const HeadRowCell = styled.div`
	display: flex;
	align-items: center;
	position: relative;
	font-size: 14px;
	line-height: 18px;
	font-weight: 500;
	white-space: nowrap;
	font-family: Hind, sans-serif;
`;

const AltText = styled.div`
	height: 50px;
	display: flex;
	align-items: center;
	justify-content: center;
	font-size: 16px;
	background: #545456;
	color: ${({theme}) => theme.colorPalette.white};
	font-weight: 500;
	font-family: "Hind", sans-serif;
`;

interface IPlayerPoolList {
	PlayerAction: React.FC<{player: IPlayer; isDisabled: boolean}>;
	isDisabled: boolean;
}

export const PlayerPoolList: React.FC<IPlayerPoolList> = ({PlayerAction, isDisabled}) => {
	const {t} = useTranslation();
	const players = useSelector(getSortedPlayers);

	const playerRowRenderer = ({index, key, style}: ListRowProps) => {
		const props = {
			style,
			key,
			player: players[index],
			PlayerAction,
			isDisabled,
		};
		return <PlayerPoolRow {...props} />;
	};

	return (
		<Wrapper>
			<HeadRow>
				<HeadRowCell>{t("player_pool.body.player")}</HeadRowCell>
				<HeadRowCell>{t("player_pool.body.action")}</HeadRowCell>
			</HeadRow>

			<Container>
				<Exist when={!players.length}>
					<AltText>{t("player_pool.table_body.empty")}</AltText>
				</Exist>
				<AutoSizer>
					{({height, width}) => (
						<List
							className="player-pool-list"
							rowCount={players.length}
							rowHeight={100}
							rowRenderer={playerRowRenderer}
							height={height}
							width={width}>
							{playerRowRenderer}
						</List>
					)}
				</AutoSizer>
			</Container>
		</Wrapper>
	);
};
